import { createApiError } from '../api/errors';
import { get, post } from '../api/coreApi';
import { clearAccessToken, setAccessToken } from './tokenStorage';
import { AuthenticatedUser } from './types';

interface LoginResponse {
  token?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  id?: number;
  role?: string;
  platformRole?: string | null;
  instanceID?: number | null;
  commandCentreAccess?: {
    enabled: boolean;
    scope: 'global' | 'instance' | 'none';
  };
  error?: string;
}

const normalizeAuthenticatedUser = (payload: Partial<AuthenticatedUser> & Record<string, unknown>): AuthenticatedUser => {
  const commandCentreAccess = payload.commandCentreAccess && typeof payload.commandCentreAccess === 'object'
    ? payload.commandCentreAccess
    : {
        enabled: false,
        scope: 'none' as const,
      };

  return {
    id: Number(payload.id || 0),
    email: String(payload.email || ''),
    first_name: payload.first_name,
    last_name: payload.last_name,
    role: String(payload.role || ''),
    platformRole: typeof payload.platformRole === 'string' ? payload.platformRole : null,
    instanceID: payload.instanceID ?? null,
    commandCentreAccess: {
      enabled: (commandCentreAccess as any).enabled === true,
      scope: ((commandCentreAccess as any).scope as 'global' | 'instance' | 'none') || 'none',
    },
  };
};

export const verifyAuthenticatedUser = async (): Promise<AuthenticatedUser> => {
  const response = await get<AuthenticatedUser>('/auth/auth');
  return normalizeAuthenticatedUser(response as unknown as Record<string, unknown>);
};

export const loginWithPassword = async (email: string, password: string): Promise<AuthenticatedUser> => {
  const response = await post<LoginResponse>('/auth/login', { email, password });

  if (response.error) {
    throw createApiError('validation', response.error, 401, response);
  }

  if (!response.token) {
    throw createApiError('unknown', 'The Elite Core server did not return an access token.', undefined, response);
  }

  setAccessToken(response.token);

  try {
    return await verifyAuthenticatedUser();
  } catch (error) {
    clearAccessToken();
    throw error;
  }
};

